import type { ReactNode } from "react";
import { Trash2, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";

type DeleteRepresentativeDialogProps = {
  isOpen: boolean;
  onValidate: () => void;
  onCancel: () => void;
  errorMessage?: string;
  isLoading: boolean;
  title: string;
  text: ReactNode;
};

function DeleteRepresentativeDialog({
  isOpen,
  onValidate,
  onCancel,
  errorMessage,
  isLoading,
  title,
  text,
}: DeleteRepresentativeDialogProps) {
  const { t } = useTranslation();

  const handleOpenChange = (open: boolean) => {
    if (open || isLoading) {
      return;
    }

    onCancel();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md"
        showCloseButton={!isLoading}
        onEscapeKeyDown={event => {
          if (isLoading) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={event => {
          if (isLoading) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-danger-50 text-danger-600">
              <Trash2 className="size-5" />
            </div>
            <div className="space-y-2">
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription className="whitespace-pre-line text-foreground">
                {text}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {errorMessage ? (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <WFPButton type="button" variant="outline" disabled={isLoading} onClick={onCancel}>
            {t("common.cancel")}
          </WFPButton>
          <WFPButton type="button" variant="destructive" disabled={isLoading} onClick={onValidate}>
            {isLoading ? <Spinner className="size-4" /> : null}
            {t("common.delete")}
          </WFPButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { DeleteRepresentativeDialog };
export type { DeleteRepresentativeDialogProps };
